"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HtmlTextTransformer = void 0;
const HtmlParser_1 = require("../../translations/translationsTransformer/HtmlParser");
/**
 * HtmlTextTransformer is a utility class designed to parse and transform HTML text into a structured format.
 * It supports both client-side and server-side environments, making it suitable for SSR (Server-Side Rendering) applications.
 *
 * @template T - The type of the transformed output.
 */
class HtmlTextTransformer {
    /**
     * Constructs an instance of HtmlTextTransformer.
     *
     * @param parseNodeFn - A function that defines how to parse a node into the desired output type.
     * @param nodeProvider - (Optional) A custom NodeProvider implementation. If not provided, a default implementation is used.
     */
    constructor(parseNodeFn, nodeProvider) {
        this.parseNodeFn = parseNodeFn;
        this.nodeProvider = nodeProvider || this.getDefaultNodeProvider();
    }
    /**
     * Provides a default implementation of NodeProvider based on the execution environment.
     *
     * @returns A NodeProvider instance suitable for either client-side or server-side rendering.
     */
    getDefaultNodeProvider() {
        if (typeof window !== 'undefined' && window.document) {
            // Client-side environment
            return new HtmlParser_1.HtmlParser();
        }
        // Server-side environment
        return {
            getNodes: (htmlText) => {
                const parser = new DOMParser();
                const document = parser.parseFromString(htmlText, 'text/html');
                return document.body.childNodes;
            },
        };
    }
    /**
     * Creates an element representation using the provided type, attributes, and children.
     *
     * @param type - The type of the HTML element (e.g., 'div', 'span').
     * @param attributes - An array of attributes for the element.
     * @param children - The children of the element, which can be text or other elements.
     * @returns The transformed representation of the element.
     */
    createElement(type, attributes = [], children = null) {
        return this.parseNodeFn(type.toLowerCase(), attributes, children);
    }
    /**
     * Extracts attributes from a DOM node and returns them as an array of NodeAttribute objects.
     *
     * @param node - The DOM node from which to extract attributes.
     * @returns An array of NodeAttribute objects representing the node's attributes.
     */
    getAttributes(node) {
        const attributeNames = node.getAttributeNames();
        const attributes = [];
        if (attributeNames.length) {
            for (const name of attributeNames) {
                attributes.push({ attribute: name, value: node.attributes[name].value });
            }
        }
        return attributes;
    }
    /**
     * Recursively processes a list of DOM nodes and transforms them into the desired output format.
     *
     * @param nodes - A NodeList of child nodes to process.
     * @returns An array of transformed nodes.
     */
    getComponents(nodes) {
        const levelChildren = [];
        for (const node of nodes) {
            const { nodeName, nodeValue } = node;
            if (nodeName === 'BR') {
                levelChildren.push(this.createElement(nodeName));
            }
            if (nodeName === '#text') {
                levelChildren.push(nodeValue || node.innerText);
            }
            else if (node.hasChildNodes()) {
                const attributes = this.getAttributes(node);
                const children = node.childNodes;
                if (children.length === 1 && !children[0].childNodes.length) {
                    const child = children[0];
                    levelChildren.push(this.createElement(nodeName, attributes, child.nodeValue || node.innerText));
                }
                else {
                    levelChildren.push(this.createElement(nodeName, attributes, this.getComponents(children)));
                }
            }
        }
        return levelChildren;
    }
    /**
     * Resolves the provided HTML text into a structured format by parsing and transforming its nodes.
     *
     * @param htmlText - The HTML text to parse and transform.
     * @returns An array of transformed nodes.
     */
    resolve(htmlText) {
        const children = this.nodeProvider.getNodes(htmlText);
        return this.getComponents(children);
    }
}
exports.HtmlTextTransformer = HtmlTextTransformer;
//# sourceMappingURL=HtmlTextTransformer.js.map